import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Typography,
} from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import { FC } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { InputField } from '../../../../components/input-field';
import {
  JsonField,
  isValidJson,
  parseJsonOrThrow,
  stringifyJsonForEditor,
} from '../../../../components/json-field';
import { SKU } from '../../../../types/sku';
import { useAuth } from '../../../../hooks/use-auth';
import { useAxios } from '../../../../hooks/use-axios';
import { ResponseData } from '../../../../types/axios';

interface SKUDuplicateDialogProps {
  open: boolean;
  onClose: () => void;
  sku: SKU;
}

export const SKUEditDetailsDialog: FC<SKUDuplicateDialogProps> = ({
  open,
  onClose,
  sku,
  ...other
}) => {
  const queryClient = useQueryClient();
  const { axios } = useAxios();
  const { tenant } = useAuth();

  const updateMutation = useMutation(async (updates: Partial<SKU>) => {
    if (!tenant) {
      throw new Error('Tenant is missing');
    }

    const url = `/skus/${sku.id}`;

    const data = {
      tenantId: tenant?.id,
      ...updates,
    };

    return axios.put<ResponseData<SKU>>(url, data);
  });

  const initialValues = {
    title: sku.title || '',
    // Whole publicMetadata is edited as free-form JSON in this dialog (like the vertical form).
    publicMetadata: stringifyJsonForEditor(sku.publicMetadata),
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      submit: null,
    },
    validationSchema: Yup.object().shape({
      title: Yup.string().required('Title is required'),
      publicMetadata: Yup.string().test('is-json', 'Invalid JSON', isValidJson),
    }),
    onSubmit: async (values, helpers) => {
      const { publicMetadata, title } = values;

      const newSKU: Partial<SKU> = {
        title,
        publicMetadata: parseJsonOrThrow(publicMetadata) as SKU['publicMetadata'],
      };

      updateMutation.mutate(newSKU, {
        onSuccess: () => {
          toast.success(`SKU's properties were updated`);
          queryClient.invalidateQueries('sku');
          onClose();
          helpers.setStatus({ success: true });
        },
        onError: (err: any) => {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err?.response?.data?.error?.message || err?.message });
        },
        onSettled: () => {
          helpers.setSubmitting(false);
        },
      });
    },
  });

  return (
    <Dialog
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          width: '100%',
        },
      }}
      TransitionProps={{
        onExited: () => formik.resetForm(),
      }}
      {...other}
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>Edit SKU Properties</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridAutoFlow: 'row',
            }}
          >
            {/* Title */}
            <Box sx={{ mb: 3 }}>
              <InputField
                error={Boolean(formik.touched.title && formik.errors.title)}
                fullWidth
                helperText={formik.touched.title && formik.errors.title}
                label="Title"
                name="title"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.title}
                required
              />
              <Typography color="textSecondary">
                It can be edited afterwards until the item of this SKU is protected.
              </Typography>
            </Box>

            {/* Metadata (free-form JSON) */}
            <JsonField
              label="Metadata"
              name="publicMetadata"
              value={formik.values.publicMetadata}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              error={Boolean(formik.touched.publicMetadata && formik.errors.publicMetadata)}
              helperText={formik.touched.publicMetadata && formik.errors.publicMetadata}
              placeholder="{}"
            />
          </Box>
          {formik.errors.submit && (
            <FormHelperText error sx={{ mb: 2 }}>
              {formik.errors.submit}
            </FormHelperText>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={onClose} variant="text">
            Cancel
          </Button>
          <LoadingButton
            loading={formik.isSubmitting}
            color="primary"
            type="submit"
            size="large"
            variant="contained"
          >
            Save
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

SKUEditDetailsDialog.defaultProps = {
  open: false,
};

SKUEditDetailsDialog.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
  sku: PropTypes.any,
};
